// Tradução do estado da engine `direct` para o que a UI exibe.
//
// A engine grava o atendimento em `cise/leads/{waId}/direct` e um índice leve em
// `cise/leadsList/{waId}`. Aqui esses dois formatos viram os objetos que o painel
// consome. Nada além deste arquivo precisa conhecer o formato do banco.

const STAGE_MAP = {
  ENTRY: "Novo",
  QUALIFICATION: "Qualificação",
  COLLECTION: "Coleta de dados",
  COLLECTION_COMPLETE: "Dados completos",
  SCHEDULING: "Agendamento",
  SCHEDULED: "Agendado",
};

const PRIORITY_MAP = {
  high: "Alta",
  medium: "Média",
  low: "Baixa",
};

function formatTime(timestamp) {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// "2026-03-10" -> "10/03/2026". A engine guarda a data no formato do banco;
// quem lê é humano.
function formatIsoDate(iso) {
  if (!iso || typeof iso !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

export function mapLeadsListItem(waId, raw) {
  const lastMessage = raw.lastMessage || {};
  const stageRaw = raw.directStage || "ENTRY";

  const hasUnread =
    lastMessage.direction === "inbound" ||
    (raw.lastInboundAt && raw.lastInboundAt > (raw.lastOutboundAt || 0));

  return {
    id: waId,
    waId,
    // A engine grava o nome assim que descobre quem é; até lá, o número.
    name: raw.displayName || waId,
    phone: waId,
    students: Array.isArray(raw.studentNames) ? raw.studentNames : [],
    owner: "Nalla",
    stage: STAGE_MAP[stageRaw] || stageRaw,
    stageRaw,
    priority: PRIORITY_MAP[raw.priority] || "Baixa",
    status: raw.standby ? "Fechado" : "Em conversa",
    standby: Boolean(raw.standby),
    lastMessageAt: lastMessage.timestamp || raw.updatedAt || 0,
    lastUserMessage: lastMessage.preview || "",
    lastMessageDirection: lastMessage.direction || "outbound",
    lastDeliveryStatus: raw.lastStatus?.status || null,
    unread: hasUnread ? 1 : 0,
    responded: !hasUnread,
    scheduled: stageRaw === "SCHEDULED",
    updatedAt: raw.updatedAt || 0,
  };
}

const WEEKDAY_LABEL = { 1: "Seg", 2: "Ter", 3: "Qua", 4: "Qui", 5: "Sex", 6: "Sáb", 7: "Dom" };
const PERIOD_LABEL = { MORNING: "Manhã", AFTERNOON: "Tarde", EVENING: "Noite" };
const APPOINTMENT_STATUS_LABEL = { CONFIRMED: "Confirmado", OFFERED: "Aguardando confirmação" };

const PROFILE_TYPE_LABEL = {
  ADULT: "Aluno adulto",
  CHILD: "Aluno",
  GUARDIAN: "Responsável",
};

// Espelha os requisitos da engine. Idade não entra: é derivada do nascimento.
const REQUIRED_FIELDS = {
  ADULT: ["fullName", "birthDate", "neighborhood"],
  CHILD: ["fullName", "birthDate", "school"],
  GUARDIAN: ["name", "neighborhood"],
};

const FIELD_LABEL = {
  fullName: "nome completo",
  name: "nome",
  birthDate: "data de nascimento",
  neighborhood: "bairro",
  school: "escola",
};

function fieldValue(profile, field) {
  return profile?.fields?.[field]?.value ?? null;
}

// O bairro do responsável vale para as crianças que ele representa — a mesma
// regra da engine, que por isso não pergunta bairro criança por criança.
function resolveNeighborhood(profile, guardian) {
  const own = fieldValue(profile, "neighborhood");
  if (own) return { value: own, inherited: false };
  if (profile.type === "CHILD" && guardian) {
    const inherited = fieldValue(guardian, "neighborhood");
    if (inherited) return { value: inherited, inherited: true };
  }
  return { value: null, inherited: false };
}

function missingFields(profile, guardian) {
  return (REQUIRED_FIELDS[profile.type] || []).filter((field) => {
    if (field === "neighborhood") return !resolveNeighborhood(profile, guardian).value;
    if (field === "name") return !(fieldValue(profile, "name") || fieldValue(profile, "fullName"));
    return !fieldValue(profile, field);
  });
}

function mapAppointment(scheduling) {
  const confirmed = scheduling?.confirmed || null;
  const offer = scheduling?.offer || null;
  const slot = confirmed || offer;
  if (!slot) return null;

  const status = confirmed ? "CONFIRMED" : "OFFERED";
  return {
    status,
    statusLabel: APPOINTMENT_STATUS_LABEL[status],
    date: slot.date || null,
    dateFormatted: slot.displayDate || formatIsoDate(slot.date),
    weekday: slot.weekdayLabel || null,
    time: slot.time || null,
    teacherName: slot.teacherName || null,
    students: confirmed?.studentNames || [],
    // Enquanto está só ofertado, o horário fica reservado por tempo limitado.
    lockExpiresAt: confirmed ? null : offer?.lockExpiresAt || null,
  };
}

function mapPreference(preferences) {
  if (!preferences) return null;
  const weekdays = (preferences.weekdays || []).map((day) => WEEKDAY_LABEL[day] || day);
  const periods = (preferences.periods || []).map((period) => PERIOD_LABEL[period] || period);

  const parts = [];
  if (preferences.explicitDate) parts.push(formatIsoDate(preferences.explicitDate));
  if (preferences.explicitTime) parts.push(`às ${preferences.explicitTime}`);
  if (weekdays.length) parts.push(weekdays.join(", "));
  if (periods.length) parts.push(periods.join(", "));
  if (preferences.afterTime) parts.push(`a partir das ${preferences.afterTime}`);
  if (preferences.beforeTime) parts.push(`até as ${preferences.beforeTime}`);

  if (!parts.length) return null;
  return { summary: parts.join(" · "), weekdays, periods };
}

// O que está acontecendo no atendimento agora, em linguagem de operador.
// Substitui as flags booleanas da engine antiga, que não existem mais.
function buildContextChips(state, profiles, guardian) {
  const chips = [];
  const scheduling = state.scheduling || {};

  if (state.standby?.active) chips.push("Em standby");
  if (scheduling.status === "AWAITING_RESPONSE") chips.push("Aguardando confirmação de horário");
  if (scheduling.status === "EXHAUSTED") chips.push("Sem horário disponível");
  if (scheduling.attempts > 1) chips.push(`${scheduling.attempts} horários ofertados`);
  if ((state.pendingAmbiguities || []).length) chips.push("Ambiguidade em aberto");

  const pending = state.pendingIntent || {};
  if (pending.kind === "ASK_FIELDS" && pending.profileId) {
    const target = profiles.find((profile) => profile.id === pending.profileId);
    const who =
      target?.fields?.fullName?.value ||
      target?.partial?.firstName ||
      target?.refName ||
      (target?.type === "GUARDIAN" ? "responsável" : "aluno");
    const labels = (pending.fields || []).map((field) => FIELD_LABEL[field] || field);
    if (labels.length) chips.push(`Aguardando ${labels.join(", ")} de ${who}`);
  }
  if (pending.kind === "ASK_QUALIFICATION") chips.push("Aguardando para quem é o curso");

  void guardian;
  return chips;
}

/**
 * @param {object|null} state  conteúdo de `cise/leads/{waId}/direct`
 */
export function mapLeadDetails(state) {
  if (!state || typeof state !== "object") return null;

  // O RTDB devolve array quando as chaves são 0,1,2… mas não guarda array vazio.
  const profiles = Array.isArray(state.profiles)
    ? state.profiles.filter(Boolean)
    : Object.values(state.profiles || {}).filter(Boolean);

  const guardian = profiles.find((profile) => profile.type === "GUARDIAN") || null;
  const learnerProfiles = profiles.filter((profile) => profile.type !== "GUARDIAN");

  const typeCounts = learnerProfiles.reduce((acc, profile) => {
    acc[profile.type] = (acc[profile.type] || 0) + 1;
    return acc;
  }, {});
  const typeIndexes = {};

  const learners = learnerProfiles.map((profile) => {
    const typeLabel = PROFILE_TYPE_LABEL[profile.type] || "Aluno";
    typeIndexes[profile.type] = (typeIndexes[profile.type] || 0) + 1;
    const isDuplicated = typeCounts[profile.type] > 1;
    const neighborhood = resolveNeighborhood(profile, guardian);
    const fullName = fieldValue(profile, "fullName");

    return {
      id: profile.id,
      type: profile.type,
      typeLabel,
      name: fullName,
      birthDate: formatIsoDate(fieldValue(profile, "birthDate")),
      age: fieldValue(profile, "age"),
      school: fieldValue(profile, "school"),
      neighborhood: neighborhood.value,
      neighborhoodInherited: neighborhood.inherited,
      // Conhecido, mas insuficiente para o requisito.
      partialFirstName: profile.partial?.firstName || null,
      knownAgeOnly: profile.partial?.ageOnly ?? null,
      missing: missingFields(profile, guardian).map((field) => FIELD_LABEL[field] || field),
      sectionLabel:
        fullName ||
        profile.partial?.firstName ||
        profile.refName ||
        (isDuplicated ? `${typeLabel} ${typeIndexes[profile.type]}` : typeLabel),
    };
  });

  return {
    guardian: guardian
      ? {
          name: fieldValue(guardian, "name") || fieldValue(guardian, "fullName") || guardian.refName || null,
          neighborhood: fieldValue(guardian, "neighborhood"),
          missing: missingFields(guardian, guardian).map((field) => FIELD_LABEL[field] || field),
        }
      : null,
    learners,
    appointment: mapAppointment(state.scheduling),
    preference: mapPreference(state.scheduling?.preferences),
    schedulingStatus: state.scheduling?.status || "IDLE",
    standby: Boolean(state.standby?.active),
    stageRaw: state.stage || null,
    stage: STAGE_MAP[state.stage] || state.stage || null,
    turnCount: state.turnCount || 0,
    contextChips: buildContextChips(state, profiles, guardian),
  };
}

export function mapHistoryToMessages(history) {
  if (!history) return [];

  return Object.values(history)
    .sort((a, b) => (a.recordedAt ?? a.timestamp ?? 0) - (b.recordedAt ?? b.timestamp ?? 0))
    .map((msg) => ({
      from: msg.role === "user" ? "lead" : "me",
      text: msg.content || msg.text?.body || msg.preview || "",
      time: formatTime(msg.timestamp),
      timestamp: msg.timestamp || 0,
      messageType: msg.messageType || "text",
      direction: msg.direction || (msg.role === "user" ? "inbound" : "outbound"),
      authorName: msg.author?.displayName || null,
    }));
}
